"use client";

import { usePush, type PushReminderKind } from "@/lib/use-push";

function reminderStateLabel(reason?: string | null, allowed?: boolean) {
  if (allowed) return "Ready when notifications are on";
  if (reason === "quiet_hours") return "Paused for quiet hours";
  if (reason === "flow_mode") return "Paused for Flow Mode";
  if (reason === "already_checked_in") return "Already handled today";
  if (reason === "already_moving") return "Momentum already started";
  return "Preview only";
}

export default function PushSection() {
  const {
    supported,
    configured,
    permission,
    subscribed,
    loading,
    subscribe,
    unsubscribe,
    reminderPreview,
    reminderLoadingKind,
    previewReminder,
    sendReminder,
  } = usePush();

  const previewing = reminderLoadingKind !== null;

  const renderTransportState = () => {
    if (!supported) {
      return (
        <p className="text-[10px] text-muted" data-testid="settings-push-support-note">
          Push delivery is not supported on this device/browser, but you can still preview reminder copy here.
        </p>
      );
    }

    if (configured === false) {
      return (
        <div className="flex items-center gap-2" data-testid="settings-push-config-note">
          <div className="w-8 h-8 rounded-lg bg-muted/15 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="stroke-muted" strokeWidth="2">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          </div>
          <div>
            <p className="text-xs font-medium text-text-light">Push delivery not configured</p>
            <p className="text-[10px] text-muted">Set VAPID keys in server env to send reminders to devices.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-text-light">
            {subscribed ? "Enabled" : "Disabled"}
          </p>
          {permission === "denied" && (
            <p className="text-[10px] text-rose mt-0.5">
              Permission blocked — update in browser settings
            </p>
          )}
        </div>
        <button
          data-testid="settings-push-toggle"
          onClick={subscribed ? unsubscribe : subscribe}
          disabled={loading || permission === "denied"}
          className={`text-xs px-4 py-2 rounded-full font-medium transition-colors
            ${subscribed
              ? "bg-secondary text-forest hover:bg-secondary/80"
              : "bg-forest text-white hover:opacity-90"
            }
            disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          {loading ? "..." : subscribed ? "Turn Off" : "Turn On"}
        </button>
      </div>
    );
  };

  const previewAction = (kind: PushReminderKind) => () => {
    void previewReminder(kind);
  };

  const sendPreview = () => {
    if (!reminderPreview?.allowed) return;
    void sendReminder(reminderPreview.kind);
  };

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm p-4" data-testid="settings-push-section">
      <h2 className="text-sm font-semibold text-text-light mb-1">Push Notifications</h2>
      <p className="text-[10px] text-muted mb-3">
        Get morning briefs, calmer reminders, and milestone celebrations
      </p>

      {renderTransportState()}

      <div className="mt-4 rounded-2xl border border-border/70 bg-bg/60 p-3" data-testid="settings-push-reminder-preview">
        <div className="mb-3">
          <p className="text-xs font-medium text-text-light">Gentle reminder previews</p>
          <p className="text-[10px] text-muted">
            Check how a check-in or recovery nudge would sound before anything gets sent.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="settings-push-preview-check-in"
            onClick={previewAction("check_in")}
            disabled={previewing}
            className="text-xs px-3 py-1.5 rounded-full border border-border text-text-light hover:bg-white disabled:opacity-50"
          >
            {reminderLoadingKind === "check_in" ? "Loading..." : "Preview check-in"}
          </button>
          <button
            type="button"
            data-testid="settings-push-preview-recovery"
            onClick={previewAction("recovery")}
            disabled={previewing}
            className="text-xs px-3 py-1.5 rounded-full border border-border text-text-light hover:bg-white disabled:opacity-50"
          >
            {reminderLoadingKind === "recovery" ? "Loading..." : "Preview recovery"}
          </button>
        </div>

        <p className="mt-2 text-[10px] text-muted">
          These stay quiet during quiet hours and while Flow Mode is on.
        </p>

        {reminderPreview && (
          <div className="mt-3 rounded-xl border border-border bg-white/70 p-3" data-testid="settings-push-reminder-card">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-medium text-text-light" data-testid="settings-push-reminder-title">
                {reminderPreview.title}
              </p>
              <span className="text-[10px] text-muted" data-testid="settings-push-reminder-state">
                {reminderStateLabel(reminderPreview.reason, reminderPreview.allowed)}
              </span>
            </div>
            <p className="mt-1 text-[11px] leading-5 text-text-light" data-testid="settings-push-reminder-body">
              {reminderPreview.body}
            </p>

            {configured === true && subscribed && reminderPreview.allowed && (
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  data-testid="settings-push-send-reminder"
                  onClick={sendPreview}
                  disabled={previewing}
                  className="text-xs px-3 py-1.5 rounded-full bg-forest text-white hover:opacity-90 disabled:opacity-50"
                >
                  {previewing ? "Sending..." : "Send to this device"}
                </button>
                <p className="text-[10px] text-muted">
                  Uses the same grounded copy shown above.
                </p>
              </div>
            )}

            {typeof reminderPreview.sent === "number" && (
              <p className="mt-2 text-[10px] text-muted" data-testid="settings-push-send-result">
                {reminderPreview.sent > 0
                  ? `Sent to ${reminderPreview.sent} device${reminderPreview.sent === 1 ? "" : "s"}.`
                  : reminderPreview.error || "No subscribed devices were available."}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
